import { Link, router, usePage } from "@inertiajs/react"
import { useTranslation } from "react-i18next"
import { AppLayout } from "@/components/layouts/AppLayout"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Pagination } from "@/components/ui/pagination"
import { ArrowLeft, ArrowRight, ArrowRightLeft, History, MapPin } from "lucide-react"
import type { InventoryEventsPageProps } from "@/types/inventory"

const eventTypeIcon = {
  stage_change: ArrowRightLeft,
  location_change: MapPin,
} as const

function getContrastColor(hexColor: string): string {
  const hex = hexColor.replace("#", "")
  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.5 ? "#000000" : "#ffffff"
}

function formatDateTime(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

function StageChip({ stage }: { stage: { name: string; color: string } }) {
  return (
    <span
      className="inline-flex px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap"
      style={{
        backgroundColor: stage.color,
        color: getContrastColor(stage.color),
      }}
    >
      {stage.name}
    </span>
  )
}

export default function InventoryEvents() {
  const { t } = useTranslation()
  const { auth, asset, assetType, events, pagination } = usePage<InventoryEventsPageProps>().props

  function goToPage(page: number) {
    router.get(`/inventory/${asset.id}/events`, { page }, { preserveState: true, preserveScroll: true })
  }

  return (
    <AppLayout
      user={auth.user}
      accounts={auth.accounts}
      currentAccountId={auth.currentAccountId}
      role={auth.role}
    >
      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="icon" asChild>
              <Link href={`/inventory/${asset.id}`}>
                <ArrowLeft className="h-4 w-4" />
              </Link>
            </Button>
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">
                {asset.code}
              </h2>
              <p className="text-muted-foreground">{t("inventory.events.title")}</p>
            </div>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              {t("inventory.events.allEvents")}
            </CardTitle>
            <CardDescription>{t("inventory.events.allEventsDescription")}</CardDescription>
          </CardHeader>
          <CardContent>
            {events.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                {t("inventory.show.noEvents")}
              </p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <Table className="table-fixed">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-40">{t("inventory.events.date")}</TableHead>
                        <TableHead className="w-40">{t("inventory.events.user")}</TableHead>
                        <TableHead className="w-56">{t("inventory.events.change")}</TableHead>
                        {assetType === "Keg" && (
                          <TableHead className="w-40">{t("inventory.form.productLabel")}</TableHead>
                        )}
                        <TableHead className="w-40">{t("inventory.form.customerLabel")}</TableHead>
                        <TableHead>{t("inventory.events.notes")}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {events.map((event, index) => (
                        <TableRow key={event.id} className={index % 2 === 1 ? "bg-muted" : ""}>
                          <TableCell className="text-sm">
                            {formatDateTime(event.created_at)}
                          </TableCell>
                          <TableCell className="truncate">
                            {event.user ? (event.user.name || event.user.email) : t("inventory.events.system")}
                          </TableCell>
                          <TableCell>
                            {(() => {
                              const Icon = eventTypeIcon[event.event_type]
                              const hasContent =
                                (event.event_type === "stage_change" && (event.from_stage || event.to_stage)) ||
                                (event.event_type === "location_change" && (event.from_location || event.to_location))

                              if (!hasContent) return "-"

                              return (
                                <div className="flex items-center gap-2">
                                  <Icon className="w-4 h-4 text-muted-foreground flex-shrink-0" aria-label={t(`inventory.events.${event.event_type}`)} />
                                  {event.event_type === "stage_change" && (
                                    <>
                                      {event.from_stage && <StageChip stage={event.from_stage} />}
                                      {event.from_stage && event.to_stage && (
                                        <ArrowRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                                      )}
                                      {event.to_stage && <StageChip stage={event.to_stage} />}
                                    </>
                                  )}
                                  {event.event_type === "location_change" && (
                                    <div className="flex items-center gap-2 text-sm">
                                      {event.from_location && <span className="font-medium">{event.from_location.name}</span>}
                                      {event.from_location && event.to_location && (
                                        <ArrowRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                                      )}
                                      {event.to_location && <span className="font-medium">{event.to_location.name}</span>}
                                    </div>
                                  )}
                                </div>
                              )
                            })()}
                          </TableCell>
                          {assetType === "Keg" && (
                            <TableCell className="truncate">
                              {event.product?.name || "-"}
                            </TableCell>
                          )}
                          <TableCell className="truncate">
                            {event.customer?.name || "-"}
                          </TableCell>
                          <TableCell className="truncate" title={event.notes || undefined}>
                            {event.notes || "-"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {pagination.pages > 1 && (
                  <div className="mt-4">
                    <Pagination pagination={pagination} onPageChange={goToPage} />
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
